<template>
  <nav class="navbar navbar-expand-lg">
    <div class="container-fluid">

      <!-- Logo (shared, always visible) -->
      <router-link class="navbar-brand" to="/">
        <img :src="headerLogo || defaultLogo" alt="logo" />
      </router-link>

      <!-- ══════════════════════════════════════════════
           MOBILE APP HEADER  (<992px — mobile + tablet)
           Replaces the hamburger with a native-app header; the bottom
           sheet (.mbs-sheet) owns this whole range now, so there's no
           in-between tablet width where the account menu is nested inside
           the collapsed hamburger nav.
      ══════════════════════════════════════════════ -->
      <div class="mnh">
        <span class="mnh-title">{{ headerTitle }}</span>
        <div class="mnh-end">

          <!-- Guest: login shortcut -->
          <router-link v-if="!auth" to="/login" class="mnh-login-btn" aria-label="تسجيل الدخول">
            <i class="pi pi-sign-in"></i>
            <span>دخول</span>
          </router-link>

          <!-- Auth: notification bell -->
          <router-link v-if="auth" to="/notifications" class="mnh-bell" aria-label="الإشعارات">
            <i class="pi pi-bell"></i>
            <span v-if="unreadCount" class="mnh-bell__badge">{{ unreadCount > 9 ? '9+' : unreadCount }}</span>
          </router-link>

          <!-- Auth: avatar button → opens bottom sheet -->
          <button
            v-if="auth"
            class="mnh-avatar-btn"
            type="button"
            @click="sheetOpen = true"
            :aria-label="user?.name"
          >
            <span class="mnh-avatar-letter">{{ user?.name?.charAt(0) || 'م' }}</span>
          </button>
        </div>
      </div>
      <!-- ══ END MOBILE APP HEADER ══ -->

      <!-- Hamburger toggle for the (now-retired) tablet-width collapsed nav
           — .mnh covers that whole range now, so .nav-toggler/.navbar-nav
           are force-hidden up to 991px (see the @media block below) and
           this button never actually appears. Left in place (harmless,
           always hidden) rather than removed, since nothing else in this
           task required deleting it. -->
      <button
        class="nav-toggler"
        :class="{ open: menuOpen }"
        @click="menuOpen = !menuOpen"
        aria-label="القائمة"
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      <!-- Desktop nav items -->
      <ul class="navbar-nav" :class="{ 'mobile-open': menuOpen }">

        <!-- Guest links -->
        <li class="nav-item" v-if="!auth">
          <router-link class="nav-link" to="/search" @click="menuOpen = false">
            🔍 ابحث عن حجزك الآن
          </router-link>
        </li>
        <li class="nav-item" v-if="!auth">
          <router-link class="navbar-login-link" to="/login" @click="menuOpen = false">
            تسجيل الدخول
          </router-link>
        </li>

        <!-- Always-visible content links -->
        <li class="nav-item">
          <router-link class="nav-link" to="/about" @click="menuOpen = false">من نحن</router-link>
        </li>
        <li class="nav-item">
          <router-link class="nav-link" to="/blogs" @click="menuOpen = false">المدونة</router-link>
        </li>

        <!-- Authenticated user dropdown — a plain, compact anchored dropdown
             (GitHub/Discord style), explicitly NOT fullscreen/mega. Visibility
             is driven entirely by sheetOpen (the same flag the mobile bottom
             sheet uses, one shared source of truth) via v-if, not Bootstrap's
             data-bs-toggle/.show mechanism — that JS is what used to position
             this panel; without it, .dd-panel needs its own explicit
             position:absolute (see CSS) or it renders unanchored/page-
             spanning. Items come from menuSections (shared with .mbs-nav
             above) so nothing is defined twice. -->
        <div class="dropdown nav-item" v-if="auth" ref="accountDropdownEl">
          <button
            class="dd-chip-btn"
            type="button"
            :aria-expanded="sheetOpen"
            @click="sheetOpen = !sheetOpen"
          >
            <span class="dd-chip-avatar"><span>{{ user?.name?.charAt(0) || 'م' }}</span></span>
            <span class="dd-chip-info">
              <span class="dd-chip-name">{{ user?.name }}</span>
              <span v-if="academicYearLabel" class="dd-chip-grade">{{ academicYearLabel }}</span>
            </span>
            <i class="pi pi-angle-down dd-chip-caret"></i>
          </button>
          <Transition name="dd-fade">
            <div class="dd-panel" v-if="sheetOpen">

            <!-- Profile section: avatar, name, academic year badge. Fixed —
                 sits outside .dd-scroll so it never scrolls with the list. -->
            <div class="dd-profile">
              <div class="dd-avatar"><span>{{ user?.name?.charAt(0) || 'م' }}</span></div>
              <div class="dd-profile-info">
                <div class="dd-profile-name">{{ user?.name }}</div>
                <div v-if="academicYearLabel" class="dd-profile-grade">{{ academicYearLabel }}</div>
              </div>
            </div>

            <!-- Scrollable body: the 3 sections only, plain single-column
                 list rows — no cards, no 2-column grid, no glass. -->
            <div class="dd-scroll">
              <template v-for="(section, sIdx) in menuSections" :key="section.key">
                <div class="dd-section-label">{{ section.title }}</div>
                <router-link
                  v-for="item in section.items"
                  :key="item.to"
                  class="dd-tile"
                  :to="item.to"
                  @click="menuOpen = false; sheetOpen = false"
                >
                  <span class="dd-tile-icon" :class="'dd-ic-' + item.color"><i :class="['pi', item.icon]"></i></span>
                  <span class="dd-tile-label">{{ item.label }}</span>
                  <span v-if="item.badge && unreadCount" class="nav-drop-badge">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
                </router-link>
                <div class="dd-divider" v-if="sIdx < menuSections.length - 1"></div>
              </template>
            </div>

            <!-- Logout — full width, red, always at the bottom, outside the
                 scrollable list. Not part of menuSections (it's an action,
                 not a route). -->
            <button type="button" class="dd-logout" @click="logout">
              <i class="pi pi-sign-out"></i> تسجيل الخروج
            </button>

            </div>
          </Transition>
        </div>

        <!-- Credit inside mobile menu -->
        <li class="nav-item nav-credit-mobile">
          <div class="nav-credit-mobile-inner">
            <span>Powered by <strong>Ahmed Hawam</strong> ⚡</span>
            <div class="nav-credit-socials">
              <a href="https://github.com/AhmedHawam9" target="_blank" rel="noopener" class="ncs-link" aria-label="GitHub">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"/></svg>
              </a>
              <a href="https://linkedin.com/in/ahmed-hawam" target="_blank" rel="noopener" class="ncs-link" aria-label="LinkedIn">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
              <a href="https://instagram.com/ahmed.hawam" target="_blank" rel="noopener" class="ncs-link" aria-label="Instagram">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              </a>
            </div>
          </div>
        </li>

      </ul>
    </div>
  </nav>

  <!-- ══════════════════════════════════════════
       MOBILE PROFILE BOTTOM SHEET
       Teleported to <body> so it layers over everything
  ══════════════════════════════════════════ -->
  <Teleport to="body">
    <!-- Backdrop -->
    <Transition name="mbs-backdrop">
      <div
        v-if="sheetOpen"
        class="mbs-backdrop"
        @click="closeSheet"
      ></div>
    </Transition>

    <!-- Sheet -->
    <Transition name="mbs-sheet">
      <div
        v-if="sheetOpen"
        class="mbs-sheet"
        :class="{ 'mbs-sheet--dragging': _dragging }"
        ref="sheetEl"
        :style="dragStyle"
        dir="rtl"
      >
        <!-- Drag handle + close button row — one of the two drag-to-dismiss
             regions (the other is .mbs-profile below). Listeners live on
             these two elements specifically (not on .mbs-sheet or
             .mbs-scroll), so menu content can never start a dismiss gesture
             by construction, not just by a runtime check. Supports both
             Touch and Pointer Events (onDragStart/Move/End dispatch on
             e.type, guarded by _activeInputMode so a platform that fires
             both never double-processes one gesture). -->
        <div
          class="mbs-top-bar"
          ref="topBarEl"
          @touchstart="onDragStart"
          @touchmove="onDragMove"
          @touchend="onDragEnd"
          @touchcancel="onDragCancel"
          @pointerdown="onDragStart"
          @pointermove="onDragMove"
          @pointerup="onDragEnd"
          @pointercancel="onDragCancel"
        >
          <div class="mbs-handle-bar"></div>
          <button class="mbs-close-btn" @click="closeSheet" aria-label="إغلاق">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <!-- Profile header — fixed, outside the scroll area, always visible.
             Second (and last) drag-to-dismiss region, alongside .mbs-top-bar:
             same handlers, same shared _dragging/_touchStartY/etc. state, so
             the two elements behave as one continuous drag area — a gesture
             tracks correctly even if it starts on one and the finger drifts
             over the other (touch events keep targeting whichever element
             the gesture began on; pointer events are pinned there via
             setPointerCapture in onDragStart). -->
        <div
          class="mbs-profile"
          @touchstart="onDragStart"
          @touchmove="onDragMove"
          @touchend="onDragEnd"
          @touchcancel="onDragCancel"
          @pointerdown="onDragStart"
          @pointermove="onDragMove"
          @pointerup="onDragEnd"
          @pointercancel="onDragCancel"
        >
          <div class="mbs-avatar">
            <span class="mbs-avatar-letter">{{ user?.name?.charAt(0) || 'م' }}</span>
          </div>
          <div class="mbs-profile-info">
            <div class="mbs-profile-name">{{ user?.name }}</div>
            <div v-if="academicYearLabel" class="mbs-profile-grade">{{ academicYearLabel }}</div>
          </div>
        </div>

        <!-- Scrollable body: menu items only. Never receives drag listeners
             at all — only .mbs-top-bar and .mbs-profile can start a dismiss
             gesture, so this always scrolls normally, never dismisses. -->
        <div class="mbs-scroll" ref="scrollEl">

          <!-- Menu items — data-driven from menuSections (shared with the
               desktop dropdown below, so routes/icons/labels are defined
               exactly once). Markup/classes are unchanged from the previous
               hardcoded version, just generated via v-for. -->
          <nav class="mbs-nav">

            <template v-for="(section, sIdx) in menuSections" :key="section.key">
              <div class="mbs-section-label">{{ section.title }}</div>

              <button
                v-for="item in section.items"
                :key="item.to"
                class="mbs-item"
                @click="navTo(item.to)"
              >
                <span class="mbs-item-icon" :class="'mbs-ic-' + item.color"><i :class="['pi', item.icon]"></i></span>
                <span class="mbs-item-label">{{ item.label }}</span>
                <span v-if="item.badge && unreadCount" class="mbs-badge">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
                <i class="pi pi-angle-left mbs-item-arrow"></i>
              </button>

              <div class="mbs-divider" v-if="sIdx < menuSections.length - 1"></div>
            </template>

            <div class="mbs-divider"></div>

            <button class="mbs-item mbs-item-logout" @click="doLogout">
              <span class="mbs-item-icon mbs-ic-red"><i class="pi pi-sign-out"></i></span>
              <span class="mbs-item-label">تسجيل الخروج</span>
            </button>

          </nav>

        </div>

      </div>
    </Transition>
  </Teleport>
</template>

<script>
import Swal from "sweetalert2";
import { computed } from "vue";
import { useAcademicYears } from "@/composables/useAcademicYears";
import { useHomepageConfig } from "@/composables/useHomepageConfig";
import { subscriptionService } from "@/services/subscriptionService.js";
import defaultLogo from "@/assets/images/favicon.png";

export default {
  name: "navbar",
  props: { forceGuest: { type: Boolean, default: false } },
  setup() {
    const { getAcademicYearName, fetchAcademicYears } = useAcademicYears();
    // Homepage Builder feature — global header chrome. Each consumer
    // (navbar/footer/HomePageRenderer) independently calls
    // fetchHomepageConfig(); the composable's module-level cache dedupes
    // this down to a single network request regardless of how many
    // components call it.
    const { getSettings, fetchHomepageConfig } = useHomepageConfig();
    fetchHomepageConfig();
    const headerSettings = computed(() => getSettings('header', { site_title: 'طارق عبدالغني عبدالباري', logo: null }));
    const headerTitle = computed(() => headerSettings.value.site_title);
    const headerLogo = computed(() => headerSettings.value.logo);
    return { getAcademicYearName, fetchAcademicYears, headerTitle, headerLogo, defaultLogo };
  },
  data() {
    return {
      menuOpen: false,
      sheetOpen: false,
      unreadCount: 0,
      // header drag-to-dismiss state
      _touchStartY: 0,
      _touchStartTime: 0,
      _dragY: 0,
      _dragging: false,
      _activeInputMode: null, // 'touch' | 'pointer' | null — guards against double-processing
      _pendingY: 0,
      _rafPending: false,
    };
  },
  computed: {
    auth() {
      return !this.forceGuest && this.$store.getters.isAuthenticated;
    },
    user() {
      return this.$store.getters.user;
    },
    academicYearLabel() {
      return this.getAcademicYearName(this.user?.academic_year_id);
    },
    // Single source of truth for the navigation items — both the mobile
    // bottom sheet (.mbs-sheet) and the desktop dropdown (.dd-panel) render
    // from this same array, so routes/icons/labels are defined exactly
    // once. Logout is intentionally NOT here — both surfaces give it its
    // own distinct full-width treatment and it isn't a route, just a
    // @click="doLogout"/"logout" action.
    menuSections() {
      return [
        {
          key: 'education', title: 'التعليم', icon: 'pi-graduation-cap', color: 'blue',
          items: [
            { label: 'الجدول الدراسي', icon: 'pi-calendar', color: 'green', to: '/schedule' },
            { label: 'نتائجي', icon: 'pi-chart-bar', color: 'blue', to: '/my_exams' },
            { label: 'محتوى الحصص', icon: 'pi-book', color: 'purple', to: '/contents' },
            { label: 'الامتحانات', icon: 'pi-file-edit', color: 'teal', to: '/exams?type=online' },
            { label: 'الواجب المنزلي', icon: 'pi-home', color: 'orange', to: '/exams?type=homework' },
          ],
        },
        {
          key: 'subscriptions', title: 'الاشتراكات', icon: 'pi-credit-card', color: 'teal',
          items: [
            { label: 'الكورسات', icon: 'pi-book', color: 'purple', to: '/courses' },
            // { label: 'السنوات الدراسية', icon: 'pi-graduation-cap', color: 'blue', to: '/academic-years' },
            { label: 'اشتراكاتي', icon: 'pi-id-card', color: 'teal', to: '/my-subscriptions' },
            { label: 'سجل المدفوعات', icon: 'pi-wallet', color: 'slate', to: '/payment-history' },
          ],
        },
        {
          key: 'account', title: 'الحساب', icon: 'pi-user', color: 'purple',
          items: [
            { label: 'الإشعارات', icon: 'pi-bell', color: 'orange', to: '/notifications', badge: true },
            { label: 'تعديل البيانات', icon: 'pi-user-edit', color: 'slate', to: '/update_data' },
            { label: 'تعديل كلمة المرور', icon: 'pi-lock', color: 'slate', to: '/update_password' },
            { label: 'مجموعة الواتساب', icon: 'pi-whatsapp', color: 'green', to: '/whatsapp_link' },
          ],
        },
      ];
    },
    dragStyle() {
      // Actively dragging: 1:1 finger tracking, no transition (any lag here
      // would fight the rAF-driven updates in onDragMove).
      if (this._dragging) {
        return { transform: `translateY(${this._dragY}px)`, transition: 'none' };
      }
      // Released without dismissing: animate smoothly back to rest —
      // requirement 8's exact transition. Dismissal itself is handled by
      // closeSheet() resetting _dragY/_dragging before sheetOpen flips, so
      // Vue's own mbs-sheet-leave-active transition takes over cleanly
      // (requirement 9 — the opening/closing v-if transition is untouched).
      return { transform: 'translateY(0)', transition: 'transform 220ms cubic-bezier(.22,.61,.36,1)' };
    },
  },
  methods: {
    closeSheet() {
      this.sheetOpen = false;
      this._dragY = 0;
      this._dragging = false;
    },

    // Outside-click-to-close (requirement 4) — only attached to `document`
    // while sheetOpen is true (see the watcher). Ignores clicks inside
    // either representation of the account menu (desktop dropdown wrapper,
    // mobile sheet/backdrop) and on the mobile trigger button itself, so it
    // never fights the toggle click or an in-progress drag gesture.
    onDocumentClick(e) {
      if (!this.sheetOpen) return;
      const insideDesktopMenu = this.$refs.accountDropdownEl?.contains(e.target);
      const insideMobileSheetUi = e.target.closest?.('.mbs-sheet, .mbs-backdrop, .mnh-avatar-btn');
      if (insideDesktopMenu || insideMobileSheetUi) return;
      this.closeSheet();
    },

    navTo(path) {
      this.closeSheet();
      this.$nextTick(() => {
        if (this.$route.fullPath !== path) this.$router.push(path);
      });
    },

    async doLogout() {
      this.closeSheet();
      await this.$nextTick();
      await this.$store.dispatch("logout");
      await Swal.fire({
        position: "center",
        icon: "success",
        title: "تم تسجيل الخروج بنجاح",
        showConfirmButton: false,
        timer: 1000,
      });
      await this.$router.replace("/");
    },

    async logout() {
      if (!this.auth) { this.$router.push("/login"); return; }
      await this.doLogout();
    },

    async loadUnreadCount() {
      try {
        const res = await subscriptionService.notifications();
        this.unreadCount = res.unread_count || 0;
      } catch { /* non-fatal */ }
    },

    // .mbs-top-bar is the ONLY drag-to-dismiss region (listeners are bound
    // there in the template, not on .mbs-sheet) — .mbs-scroll never receives
    // these handlers at all, so menu content can never start a dismiss
    // gesture by construction. Dragging engages immediately on press inside
    // the header (nothing there is scrollable, so there's no reason to wait
    // for movement first) and is clamped so it can never go negative —
    // dragging upward just holds the sheet at rest (requirement 4).
    //
    // Supports both Touch and Pointer Events. Each handler resolves its own
    // input "point" from e.type and checks _activeInputMode so a platform
    // that dispatches both for one physical gesture never double-processes
    // it — the first event to arrive claims the gesture; the other input
    // type is ignored until it ends.
    _dragPoint(e, mode) {
      if (mode === 'touch') return e.touches[0] || e.changedTouches?.[0] || null;
      return e;
    },
    onDragStart(e) {
      const mode = e.type.startsWith('touch') ? 'touch' : 'pointer';
      if (this._activeInputMode && this._activeInputMode !== mode) return;

      const point = this._dragPoint(e, mode);
      if (!point) return;

      this._activeInputMode = mode;
      this._touchStartY = point.clientY;
      this._touchStartTime = e.timeStamp;
      this._dragY = 0;
      this._pendingY = point.clientY;
      this._rafPending = false;
      this._dragging = true;

      if (mode === 'pointer' && e.target?.setPointerCapture) {
        try { e.target.setPointerCapture(e.pointerId); } catch (_) { /* non-fatal */ }
      }
    },
    onDragMove(e) {
      const mode = e.type.startsWith('touch') ? 'touch' : 'pointer';
      if (!this._dragging || this._activeInputMode !== mode) return;

      const point = this._dragPoint(e, mode);
      if (!point) return;

      // Prevent page scrolling while dragging the header (requirement 6) —
      // touch-action:none on .mbs-top-bar (CSS) covers pointer events; this
      // covers touch events on browsers that still need the explicit call.
      if (e.cancelable) e.preventDefault();

      // rAF-throttled: multiple move events can fire per frame on some
      // devices — only the latest Y matters, applied at most once per frame
      // so the sheet follows the finger smoothly without redundant re-renders.
      this._pendingY = point.clientY;
      if (this._rafPending) return;
      this._rafPending = true;
      requestAnimationFrame(() => {
        this._rafPending = false;
        if (!this._dragging) return;
        const delta = this._pendingY - this._touchStartY;
        // Clamp at 0 — never allow dragging the sheet above its resting
        // position (requirement 4); an upward push just holds it at rest.
        this._dragY = Math.max(0, delta);
      });
    },
    onDragEnd(e) {
      const mode = e.type.startsWith('touch') ? 'touch' : 'pointer';
      if (this._activeInputMode !== mode) return;

      const wasDragging = this._dragging;
      const dragY = this._dragY;
      const elapsed = Math.max(1, (e.timeStamp ?? Date.now()) - this._touchStartTime);
      const velocity = dragY / elapsed; // px/ms — a quick flick dismisses even short of the distance thresholds

      this._dragging = false;
      this._activeInputMode = null;

      if (!wasDragging) return;

      const sheetHeight = this.$refs.sheetEl?.offsetHeight || 0;
      const shouldDismiss = dragY > 120
        || (sheetHeight > 0 && dragY > sheetHeight * 0.25)
        || (dragY > 10 && velocity > 0.5);

      if (shouldDismiss) {
        this.closeSheet();
      } else {
        this._dragY = 0;
      }
    },
    onDragCancel() {
      this._dragging = false;
      this._activeInputMode = null;
      this._dragY = 0;
    },
  },
  watch: {
    $route() { this.closeSheet(); },
    // Single source of truth for both the mobile bottom sheet (.mbs-sheet)
    // and the desktop dropdown (.dd-panel) — requirement 10. Escape/back-
    // button close (via _onKeyDown/_onPopState, already existing) and the
    // new outside-click handler now apply to both automatically since both
    // are driven by this one flag.
    sheetOpen(open) {
      if (open) {
        // Only the full-screen mobile sheet needs body scroll locked behind
        // it — freezing the page for a small anchored desktop dropdown
        // would be wrong, so this is gated to the same breakpoint .mnh uses.
        if (window.matchMedia('(max-width: 991px)').matches) {
          document.body.style.overflow = 'hidden';
        }
        window.addEventListener('keydown', this._onKeyDown);
        window.addEventListener('popstate', this._onPopState);
        document.addEventListener('click', this.onDocumentClick);
      } else {
        document.body.style.overflow = '';
        window.removeEventListener('keydown', this._onKeyDown);
        window.removeEventListener('popstate', this._onPopState);
        document.removeEventListener('click', this.onDocumentClick);
      }
    },
  },
  created() {
    if (this.auth) {
      this.fetchAcademicYears();
      this.loadUnreadCount();
    }
    this._onKeyDown = (e) => { if (e.key === 'Escape') this.closeSheet(); };
    this._onPopState = () => { if (this.sheetOpen) this.closeSheet(); };
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this._onKeyDown);
    window.removeEventListener('popstate', this._onPopState);
    document.removeEventListener('click', this.onDocumentClick);
    document.body.style.overflow = '';
  },
};
</script>

<style scoped>
/* ── Subscriptions: notification bell + badges ── */
.mnh-bell {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 12px;
  background: rgba(91,95,239,.08);
  color: #5B5FEF;
  text-decoration: none;
  margin-inline-end: 8px;
}
.mnh-bell__badge {
  position: absolute;
  top: -4px;
  inset-inline-end: -4px;
  background: #ef4444;
  color: #fff;
  font-size: .62rem;
  font-weight: 800;
  min-width: 16px;
  height: 16px;
  padding: 0 3px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.mbs-badge {
  background: #ef4444;
  color: #fff;
  font-size: .68rem;
  font-weight: 800;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-inline-start: auto;
}
.nav-drop-badge {
  background: #ef4444;
  color: #fff;
  font-size: .62rem;
  font-weight: 800;
  padding: 1px 6px;
  border-radius: 9px;
  margin-inline-start: 6px;
}

/* ══════════════════════════════════════════
   MOBILE APP HEADER (.mnh)
   Shown on <992px (mobile + tablet — the bottom sheet owns this whole
   range now, so there's no in-between width where the account menu is
   awkwardly nested inside the collapsed hamburger nav), hidden on desktop.
══════════════════════════════════════════ */
.mnh {
  display: none; /* desktop: off */
}

@media (max-width: 991px) {
  /* ── Show mobile header, hide desktop elements ── */
  .mnh {
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: space-between;
    padding: 0 0.5rem;
    gap: 0.75rem;
    min-width: 0; /* allow the title to shrink instead of clipping */
  }

  /* Hide the desktop hamburger toggle */
  .nav-toggler { display: none !important; }

  /* Hide the desktop navbar-nav entirely */
  .navbar-nav { display: none !important; }

  /* ── Brand title ── */
  .mnh-title {
    font-family: 'Cairo', 'Almarai', sans-serif;
    font-size: 1rem;
    font-weight: 800;
    color: #1A1A1A;
    line-height: 1.3;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: center;
  }

  /* ── End slot ── */
  .mnh-end {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
  }

  /* ── Guest login button ── */
  .mnh-login-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.38rem 0.85rem;
    background: linear-gradient(135deg, #5B5FEF, #7C4DFF);
    color: #fff !important;
    border-radius: 99px;
    font-size: 0.82rem;
    font-weight: 700;
    text-decoration: none;
    transition: opacity 0.15s ease, transform 0.15s ease;
    -webkit-tap-highlight-color: transparent;
    font-family: 'Cairo', 'Almarai', sans-serif;
  }
  .mnh-login-btn:active { transform: scale(0.95); opacity: 0.9; }
  .mnh-login-btn i { font-size: 0.8rem; }

  /* ── Auth avatar button ── */
  .mnh-avatar-btn {
    width: 38px;
    height: 38px;
    min-height: 38px;
    border-radius: 50%;
    background: linear-gradient(135deg, #5B5FEF, #7C4DFF);
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 14px rgba(91, 95, 239, 0.35);
    transition: transform 0.15s ease, box-shadow 0.15s ease;
    -webkit-tap-highlight-color: transparent;
    padding: 0;
  }
  .mnh-avatar-btn:active { transform: scale(0.92); box-shadow: 0 2px 8px rgba(91,95,239,.2); }

  .mnh-avatar-letter {
    font-size: 1rem;
    font-weight: 800;
    color: #fff;
    font-family: 'Cairo', 'Almarai', sans-serif;
    line-height: 1;
  }

}

/* ══════════════════════════════════════════
   DESKTOP CREDIT BLOCKS (unchanged)
══════════════════════════════════════════ */
.nav-credit {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex: 1;
  justify-content: center;
}

.nav-credit-text {
  font-size: 0.78rem;
  color: #6B7280;
  font-family: 'Cairo', sans-serif;
  white-space: nowrap;
  letter-spacing: 0.01em;
}
.nav-credit-text strong { color: #5B5FEF; font-weight: 800; }

.nav-credit-socials {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}
.ncs-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  color: #9CA3AF;
  background: transparent;
  transition: color 0.2s, background 0.2s, transform 0.2s;
  text-decoration: none;
}
.ncs-link svg { width: 15px; height: 15px; }
.ncs-link:hover { color: #5B5FEF; background: rgba(91,95,239,0.08); transform: translateY(-2px); }

.nav-credit-mobile { display: none; }
.nav-credit-mobile-inner {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.78rem;
  color: #6B7280;
  font-family: 'Cairo', sans-serif;
  border-top: 1px solid rgba(91,95,239,0.08);
  margin-top: 0.25rem;
  padding-top: 0.75rem;
}
.nav-credit-mobile-inner strong { color: #5B5FEF; font-weight: 800; }

@media (max-width: 991px) {
  .nav-credit { display: none; }
  .nav-credit-mobile { display: block; }
}

/* ══════════════════════════════════════════
   ACCOUNT DROPDOWN — a plain, compact anchored dropdown (GitHub/Discord
   style). Explicitly NOT fullscreen, NOT a mega panel: fixed small width,
   position:absolute anchored to its own position:relative wrapper. This is
   NOT .mbs-* (the mobile bottom sheet — untouched, only reachable at
   <=768px via .mnh; the two share only menuSections' data, nothing else).

   Bug this fixes: this dropdown used to rely on Bootstrap's
   data-bs-toggle="dropdown" + Popper JS to compute its position. That was
   removed (sheetOpen now drives visibility via v-if instead), which also
   removed the ONLY thing ever setting this panel's position — so it had no
   anchoring at all and rendered as an unpositioned, page-spanning block.
   .dropdown/.dd-panel below now position it explicitly, with no
   dependency on Bootstrap's JS whatsoever. ── */
.dropdown.nav-item { position: relative; }

/* Trigger "chip" — avatar + name + academic year badge + caret. */
.dd-chip-btn {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.35rem 0.75rem 0.35rem 0.5rem;
  border: 1px solid transparent;
  border-radius: 99px;
  background: transparent;
  cursor: pointer;
  transition: background 0.18s ease, border-color 0.18s ease;
}
.dd-chip-btn:hover, .dd-chip-btn[aria-expanded="true"] {
  background: rgba(91,95,239,0.07);
  border-color: rgba(91,95,239,0.14);
}
.dd-chip-avatar {
  width: 30px; height: 30px; border-radius: 50%; flex-shrink: 0;
  background: linear-gradient(135deg, #5B5FEF, #7C4DFF);
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-weight: 800; font-size: 0.8rem;
}
.dd-chip-info { display: flex; flex-direction: column; align-items: flex-start; line-height: 1.2; }
.dd-chip-name { font-size: 0.85rem; font-weight: 700; color: #1A1A1A; }
.dd-chip-grade { font-size: 0.68rem; font-weight: 600; color: #5B5FEF; opacity: 0.85; }
.dd-chip-caret { font-size: 0.65rem; color: #9CA3AF; }

/* Explicitly positioned, small, fixed-width — never fullscreen. Deliberately
   does NOT carry the global "dropdown-menu" class — that class has
   !important rules elsewhere (padding, border-radius, box-shadow,
   background+blur, its own "dropIn" animation, and — at 769-991px — a
   position:relative override) that fought this panel's own styling; giving
   this element its own unique class sidesteps all of it instead of trying
   to out-specificity !important rules. */
.dd-panel {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 1050;
  display: flex;
  flex-direction: column;
  width: 300px;
  max-width: calc(100vw - 2rem);
  max-height: 70vh;
  margin: 0;
  padding: 0;
  border: 1px solid rgba(15,23,42,0.08);
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 16px 40px rgba(15,23,42,0.14), 0 4px 12px rgba(15,23,42,0.06);
  overflow: hidden; /* clips to the rounded corners; .dd-scroll below owns actual scrolling */
}

/* Opening/closing animation — plain fade only, no translateY/scale (that's
   deliberately reserved for the mobile sheet's drag-to-dismiss feel; this
   is a plain dropdown, not a sheet). */
.dd-fade-enter-active,
.dd-fade-leave-active { transition: opacity 0.15s ease; }
.dd-fade-enter-from,
.dd-fade-leave-to { opacity: 0; }

/* Fixed header — outside .dd-scroll, never scrolls with the list. */
.dd-profile {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid rgba(15,23,42,0.07);
  flex-shrink: 0;
}
.dd-avatar {
  width: 38px; height: 38px; border-radius: 50%; flex-shrink: 0;
  background: linear-gradient(135deg, #5B5FEF, #7C4DFF);
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-weight: 800; font-size: 0.95rem;
  box-shadow: 0 6px 16px rgba(91,95,239,0.3);
}
.dd-profile-name { font-size: 0.86rem; font-weight: 800; color: #1A1A1A; }
.dd-profile-grade {
  display: inline-block; margin-top: 0.15rem;
  font-size: 0.66rem; font-weight: 700; color: #5B5FEF;
  background: rgba(91,95,239,0.09);
  border: 1px solid rgba(91,95,239,0.18);
  border-radius: 99px; padding: 0.08rem 0.5rem;
}

/* Scrollable body — the ONLY part that scrolls once content exceeds the
   max-height cap on .dd-panel; the header above and the logout button
   below both stay fixed in place. */
.dd-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 0.35rem 0;
}

.dd-section-label {
  font-size: 0.68rem; font-weight: 700; color: #9CA3AF;
  text-transform: uppercase; letter-spacing: 0.04em;
  padding: 0.5rem 1rem 0.3rem;
}
.dd-divider { height: 1px; background: rgba(15,23,42,0.06); margin: 0.35rem 0.75rem; }

/* Plain list row — icon + title, flat, no card/shadow/lift. */
.dd-tile {
  display: flex; align-items: center; gap: 0.65rem;
  min-height: 42px;
  padding: 0 1rem;
  border: none; background: transparent;
  color: #1F2937; font-size: 0.85rem; font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  width: 100%;
  transition: background 0.15s ease;
}
.dd-tile-icon {
  width: 22px; height: 22px; border-radius: 6px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.72rem;
}
.dd-tile-label { flex: 1; }
.dd-tile:hover { background: rgba(91,95,239,0.06); }

.dd-ic-blue   { background: rgba(59, 130, 246, 0.12); color: #3B82F6; }
.dd-ic-purple { background: rgba(91, 95, 239, 0.12);  color: #5B5FEF; }
.dd-ic-teal   { background: rgba(20, 184, 166, 0.12); color: #14B8A6; }
.dd-ic-orange { background: rgba(249, 115, 22, 0.12); color: #F97316; }
.dd-ic-green  { background: rgba(34, 197, 94, 0.12);  color: #22C55E; }
.dd-ic-slate  { background: rgba(100, 116, 139, 0.12);color: #64748B; }
.dd-ic-red    { background: rgba(239, 68, 68, 0.12);  color: #EF4444; }

/* Logout — fixed at the bottom, outside .dd-scroll, full width, red. */
.dd-logout {
  flex-shrink: 0;
  display: flex; align-items: center; justify-content: center; gap: 0.5rem;
  width: calc(100% - 1.6rem);
  margin: 0.4rem 0.8rem 0.8rem;
  padding: 0.6rem 1rem;
  border: none; border-radius: 10px;
  background: rgba(239,68,68,0.08);
  color: #EF4444;
  font-size: 0.85rem; font-weight: 800;
  cursor: pointer;
  transition: background 0.15s ease;
}
.dd-logout:hover { background: rgba(239,68,68,0.14); }

/* ══════════════════════════════════════════
   MOBILE PROFILE BOTTOM SHEET  (.mbs-*)
   These are NOT scoped because Teleport renders
   outside this component's DOM subtree.
   We rely on unique class names for isolation.
══════════════════════════════════════════ */
</style>

<style>
/* Hard safety guard: .mbs-sheet/.mbs-backdrop are v-if="sheetOpen" with NO
   width check of their own (only their mobile trigger button was ever
   width-gated) — sheetOpen is shared with the desktop dropdown's trigger
   too, so opening the desktop chip would ALSO mount the full-screen mobile
   sheet+backdrop behind it. This is the actual cause of "desktop still
   shows the mobile bottom sheet": both were rendering simultaneously.
   This unconditionally hides them at >=992px regardless of JS state. */
@media (min-width: 992px) {
  .mbs-backdrop,
  .mbs-sheet {
    display: none !important;
  }
}

/* ── Backdrop ── */
.mbs-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1200;
  background: rgba(0, 0, 0, 0.52);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}

/* ── Sheet ── */
.mbs-sheet {
  position: fixed;
  max-height: 98dvh;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1201;
  background: #fff;
  border-radius: 28px 28px 0 0;
  box-shadow: 0 -8px 60px rgba(0, 0, 0, 0.18);
  font-family: 'Cairo', 'Almarai', sans-serif;
  direction: rtl;
  overflow: hidden;
  will-change: transform;
  display: flex;
  flex-direction: column;
}

/* While the header is being dragged: lock the menu's own scrolling (so any
   momentum scroll can't keep running underneath the drag) and prevent text
   selection — requirement 6. The drag itself never touches .mbs-scroll, but
   this keeps everything else on the sheet inert while the gesture is live. */
.mbs-sheet--dragging {
  user-select: none;
  -webkit-user-select: none;
}
.mbs-sheet--dragging .mbs-scroll {
  overflow: hidden;
}

/* ── Scrollable body (menu only): momentum scroll on iOS, and
   overscroll-behavior keeps a rubber-band bounce here from chaining to the
   page behind the sheet.
   flex: 1 is the actual fix for "menu taller than viewport, last items cut
   off" — without it this element has no flex-grow, so in a flex column it
   just sizes to its own content's natural (unconstrained) height instead of
   filling the remaining space under .mbs-top-bar/.mbs-profile. Since
   .mbs-sheet has overflow:hidden, that excess content was silently clipped
   there instead of ever becoming scrollable inside .mbs-scroll itself.
   min-height: 0 is required alongside it — a flex item's default
   min-height:auto would otherwise still refuse to shrink below its content
   size, defeating overflow-y:auto the same way. ── */
.mbs-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-y: contain;
}

/* ── Top bar: handle + close — the drag-to-dismiss region.
   touch-action: none tells the browser never to handle default touch
   gestures (scroll/zoom) here at all; JS (onDragStart/Move/End) owns 100%
   of what happens on touch/pointer input starting inside this element. ── */
.mbs-top-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px 16px 8px;
  position: relative;
  flex-shrink: 0;
  touch-action: none;
}

.mbs-handle-bar {
  width: 40px;
  height: 5px;
  background: #E5E7EB;
  border-radius: 99px;
}

.mbs-close-btn {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  width: 34px;
  height: 34px;
  min-height: 34px !important;
  border-radius: 50%;
  background: #F3F4F6;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6B7280;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.15s ease, transform 0.15s ease;
  margin-top: 15px;
}
.mbs-close-btn:active { background: #E5E7EB; transform: translateY(-50%) scale(0.9); }
.mbs-close-btn svg { width: 16px; height: 16px; }

/* ── Profile section — fixed, sits above .mbs-scroll, never scrolls.
   Drag-to-dismiss region alongside .mbs-top-bar — touch-action: none for
   the same reason (JS owns 100% of touch/pointer handling here). ── */
.mbs-profile {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.35rem 1.1rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  flex-shrink: 0;
  touch-action: none;
}

.mbs-avatar {
  width: 58px;
  height: 58px;
  border-radius: 50%;
  background: linear-gradient(135deg, #5B5FEF 0%, #7C4DFF 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 8px 24px rgba(91, 95, 239, 0.3);
}

.mbs-avatar-letter {
  font-size: 1.55rem;
  font-weight: 900;
  color: #fff;
  line-height: 1;
}

.mbs-profile-name {
  font-size: 1.08rem;
  font-weight: 800;
  color: #111827;
  line-height: 1.2;
  margin-bottom: 0.35rem;
}

.mbs-profile-grade {
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 700;
  color: #5B5FEF;
  background: rgba(91, 95, 239, 0.09);
  border: 1px solid rgba(91, 95, 239, 0.18);
  border-radius: 99px;
  padding: 0.18rem 0.7rem;
}

/* ── Nav list ── */
.mbs-nav {
  padding: 0.55rem 0 calc(0.55rem + env(safe-area-inset-bottom));
}

.mbs-section-label {
  font-size: 0.68rem;
  font-weight: 700;
  color: #9CA3AF;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  padding: 0.4rem 1.35rem 0.2rem;
}

.mbs-item {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  width: 100%;
  padding: 0.82rem 1.35rem;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: right;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.15s ease;
  position: relative;
}

.mbs-item:active {
  background: rgba(0, 0, 0, 0.04);
}

.mbs-item-icon {
  width: 40px;
  height: 40px;
  border-radius: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.05rem;
  flex-shrink: 0;
  transition: transform 0.15s ease;
}
.mbs-item:active .mbs-item-icon {
  transform: scale(0.9);
}

.mbs-ic-blue   { background: rgba(59, 130, 246, 0.1);  color: #3B82F6; }
.mbs-ic-purple { background: rgba(91, 95, 239, 0.1);   color: #5B5FEF; }
.mbs-ic-teal   { background: rgba(20, 184, 166, 0.1);  color: #14B8A6; }
.mbs-ic-orange { background: rgba(249, 115, 22, 0.1);  color: #F97316; }
.mbs-ic-green  { background: rgba(34, 197, 94, 0.1);   color: #22C55E; }
.mbs-ic-slate  { background: rgba(100, 116, 139, 0.1); color: #64748B; }
.mbs-ic-red    { background: rgba(239, 68, 68, 0.1);   color: #EF4444; }

.mbs-item-label {
  flex: 1;
  font-size: 0.97rem;
  font-weight: 600;
  color: #1F2937;
}

.mbs-item-logout .mbs-item-label {
  color: #EF4444;
}

.mbs-item-arrow {
  color: #D1D5DB;
  font-size: 0.85rem;
}

.mbs-divider {
  height: 1px;
  background: rgba(0, 0, 0, 0.05);
  margin: 0.35rem 1.35rem;
}

/* ── Transitions ── */
.mbs-backdrop-enter-active { transition: opacity 0.28s ease; }
.mbs-backdrop-leave-active { transition: opacity 0.22s ease; }
.mbs-backdrop-enter-from,
.mbs-backdrop-leave-to   { opacity: 0; }

.mbs-sheet-enter-active {
  transition: transform 0.38s cubic-bezier(0.32, 0.72, 0, 1);
}
.mbs-sheet-leave-active {
  transition: transform 0.26s cubic-bezier(0.4, 0, 1, 1);
}
.mbs-sheet-enter-from,
.mbs-sheet-leave-to { transform: translateY(100%); }
</style>
